import { useEffect, useState } from "react";
import RoutesTable from "./RoutesTable";
import RouteDrawer from "./RouteDrawer";
import useRouteScopeRules from "hooks/route-rules/useRouteScopeRules";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import { Col, Row, Spinner } from "react-bootstrap";
import { Empty, Input } from "antd";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useDebounce from "hooks/route-rules/useDebounce";
import BulkRoutesDrawer from "./BulkRoutesDrawer";

export default function RoutesPage() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    loading,
    routes,
    addRouteRule,
    bulkImportRoutes,
    patchRouteRule,
    deleteRouteRuleById,
    fetchAllRouteRules,
    searchRouteRules,
    totalPages,
    totalElements
  } = useRouteScopeRules(page, pageSize);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showBulkDrawer, setShowBulkDrawer] = useState(false);

  // search
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchRequest = debouncedSearchTerm.trim().length >= 3;

  // trigger search
  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();

    if (trimmed.length === 0) {
      fetchAllRouteRules();
      return;
    }

    if (trimmed.length > 0 && trimmed.length < 3) {
      fetchAllRouteRules();
      return;
    }

    searchRouteRules(trimmed, page, pageSize);
  }, [debouncedSearchTerm, page, pageSize, fetchAllRouteRules, searchRouteRules]);

  const handleCreateRoute = async (newRoute) => {
    console.log("Creating route:", newRoute);
    setIsSubmitting(true);
    try {
      await addRouteRule(newRoute);
    } catch (err) {
      console.error("Failed to create route", err);
    } finally {
      setIsCreating(false);
      setIsSubmitting(false);
    }
  };

  // Update route
  const handlePatchRoute = async (patches, routeId) => {
    setIsSubmitting(true);
    try {
      await patchRouteRule(patches, routeId);
    } catch (err) {
      // toast.error("Failed to update route");
      console.error(err);
    } finally {
      setSelectedRoute(null);
      setIsSubmitting(false);
    }
  };

  // delete route
  const handleDeleteRoute = async () => {
    setIsSubmitting(true);
    try {
      await deleteRouteRuleById(routeToDelete);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
      setSearchTerm("");
    }
  };

  return (
   <>
      <WidgetSectionTitle
        icon="route"
        title="Gateway Routes"
        subtitle="View and edit gateway routes and their authorization rules."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="d-flex justify-content-end mb-3">
        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={() => setIsCreating(true)}
          className="text-xs fw-bold me-2"
          style={{ color: "#fff" }}
        >
          <span className="ms-1">Add New Route</span>
        </IconButton>
        <IconButton
          variant="info"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={() => setShowBulkDrawer(true)}
          className="text-xs fw-bold"
          style={{ color: "#fff" }}
        >
          <span className="ms-1">Add Bulk Routes</span>
        </IconButton>
      </div>

      {/* ================= SEARCH & RESULTS ROW (Aligned to Snippet) ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder='Search by "path" or "description" (min. 3 chars)...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              allowClear
              style={{
                borderRadius: 12,
                height: 30, // Exact height from snippet
                flex: "1 1 300px",
                maxWidth: 300,
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                borderColor: searchTerm.length > 0 && searchTerm.length < 3 ? "#ffa940" : ""
              }}
              suffix={
                loading && isSearchRequest ? (
                  <Spinner animation="border" size="sm" variant="success" style={{ width: 12, height: 12 }} />
                ) : searchTerm.length > 0 && searchTerm.length < 3 && (
                  <small className="text-warning" style={{ fontSize: "10px" }}>
                    Type {3 - searchTerm.length} more...
                  </small>
                )
              }
            />
          </div>
        </Col>

        {/* Results Count - Aligned to filter baseline */}
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{routes.length}</span> of <span className="fw-semibold text-dark">{totalElements}</span>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE AREA ================= */}
      {loading && routes.length === 0 ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : routes && routes.length === 0 ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Empty
            description={
              searchTerm.trim()
                ? `No routes found for "${searchTerm.trim()}"`
                : "No routes have been created yet"
            }
          />
        </div>
      ) : (
        <RoutesTable
          routes={routes}
          onEdit={(route) => setSelectedRoute(route)}
          setShowDeleteModal={setShowDeleteModal}
          setRouteToDelete={setRouteToDelete}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalElements={totalElements}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      )}

      {selectedRoute && (
        <RouteDrawer
          route={selectedRoute}
          show={!!selectedRoute}
          onClose={() => setSelectedRoute(null)}
          onSave={handlePatchRoute}
          isSubmitting={isSubmitting}
        />
      )}

      {isCreating && (
        <RouteDrawer
          route={null}
          show={isCreating}
          onClose={() => setIsCreating(false)}
          onSave={handleCreateRoute}
          isSubmitting={isSubmitting}
        />
      )}

      <ConfirmDeleteModal
        itemName="route"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDeleteRoute}
        loading={isSubmitting}
      />

      <BulkRoutesDrawer
        show={showBulkDrawer}
        onClose={() => setShowBulkDrawer(false)}
        bulkImportRoutes={bulkImportRoutes}
      />
    </>
  );
}
